import { unsafeCSS, html } from "lit";
import { property } from "lit/decorators.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { requiredValidator, minLengthValidator, maxLengthValidator, customValidator } from "../../utilities/validators.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { LeafElement } from "../leaf-element.js";
import { LeafValidationMessage } from "../validation-message/validation-message.component.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  --leaf-textarea-box-shadow-color: var(--leaf-color-border-active-outline);\n  display: grid;\n  position: relative;\n}\n:host .leaf-c-textarea__wrapper {\n  display: flex;\n  flex-direction: column;\n  margin: auto;\n  width: calc(100% - var(--leaf-spacing-8));\n}\n:host ::slotted([slot=textarea]) {\n  font-family: var(--leaf-typography-body-default-font-family);\n  font-size: var(--leaf-typography-body-default-font-size);\n  font-weight: var(--leaf-typography-body-default-font-weight);\n  line-height: var(--leaf-typography-body-default-line-height);\n  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);\n          text-decoration: var(--leaf-typography-body-default-text-decoration);\n  text-transform: var(--leaf-typography-body-default-text-transform);\n  align-self: center;\n  border: var(--leaf-border-width-default) solid var(--leaf-color-border-strong);\n  border-radius: var(--leaf-spacing-8);\n  height: calc(var(--leaf-spacing-16) * 3.5);\n  margin: var(--leaf-spacing-4) 0;\n  padding: var(--leaf-spacing-16);\n  width: 100%;\n}\n:host ::slotted([slot=textarea])::-moz-placeholder {\n  color: var(--leaf-color-content-subtle);\n}\n:host ::slotted([slot=textarea])::placeholder {\n  color: var(--leaf-color-content-subtle);\n}\n:host ::slotted([slot=textarea]:focus),\n:host ::slotted([slot=textarea]:focus-visible),\n:host ::slotted([slot=textarea]:active) {\n  box-shadow: 0 0 0 calc(var(--leaf-border-width-button-default) * 1) var(--leaf-color-border-knockout), 0 0 0 calc(var(--leaf-border-width-button-default) * 2) var(--leaf-textarea-box-shadow-color);\n  outline: 0;\n}\n:host ::slotted([slot=textarea][invalid]) {\n  --leaf-textarea-box-shadow-color: var(--leaf-color-border-error);\n  border-color: var(--leaf-color-border-error);\n}\n:host ::slotted([slot=textarea]:-moz-read-only) {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n  color: var(--leaf-color-content-disabled) !important;\n  pointer-events: none;\n}\n:host ::slotted([slot=textarea]:disabled),\n:host ::slotted([slot=textarea]:read-only) {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n  color: var(--leaf-color-content-disabled) !important;\n  pointer-events: none;\n}\n:host ::slotted([slot=textarea]:-moz-read-only) {\n  color: initial !important;\n}\n:host ::slotted([slot=textarea]:read-only) {\n  color: initial !important;\n}\n:host ::slotted([slot=note]) {\n  margin-top: var(--leaf-spacing-4);\n}\n\n:host([success-opt-in]) ::slotted([slot=textarea][valid]) {\n  --leaf-textarea-box-shadow-color: var(--leaf-color-border-success);\n  border-color: var(--leaf-color-border-success);\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let createUniqueId = 0;
const _LeafTextarea = class _LeafTextarea extends e(FormControlMixin(LeafElement)) {
  constructor() {
    super(...arguments);
    this.internals = this.attachInternals();
    this.validation = new ValidationController(this, {
      validators: [requiredValidator, minLengthValidator, maxLengthValidator, customValidator],
      nativeTarget: () => this.textarea
    });
    this.wrap = "soft";
    this.successOptIn = false;
  }
  get textarea() {
    return this.querySelector("textarea");
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    super.connectedCallback();
    this.insertAdjacentHTML("afterbegin", this.createTextarea());
    this.validation.validate(this.value ? this.value : void 0);
  }
  willUpdate() {
    if (this.error) {
      this.textarea.setCustomValidity(this.error);
    } else {
      this.textarea.setCustomValidity("");
    }
    this.validateInput();
  }
  async updated(changedProperties) {
    await this.updateComplete;
    if (this.textarea) {
      for (const [key, _value] of changedProperties) {
        const property2 = String(key);
        if (typeof this[property2] !== "undefined") {
          if (this[property2]) {
            this.textarea.setAttribute(property2, String(this[property2]));
          } else {
            this.textarea.removeAttribute(property2);
          }
        }
      }
      this.setFormControlElement(this.textarea);
    }
  }
  async firstUpdated(changedProperties) {
    var _a;
    super.firstUpdated(changedProperties);
    await this.updateComplete;
    (_a = this.textarea).id || (_a.id = `leaf-textarea-${createUniqueId++}`);
    if (this.note) {
      this.note.id = `${this.textarea.id}-note`;
      this.textarea.setAttribute("aria-describedby", this.note.id);
    }
  }
  createTextarea() {
    return `<textarea
      class="leaf-c-textarea__textarea"
      slot="textarea"
      ${this.id ? `id="${this.id}-leaf"` : ""}
      ${this.required ? "required" : ""}
      ${this.readonly ? "readonly" : ""}
      ${this.disabled ? "disabled" : ""}
      ${this.invalid ? `invalid="${this.invalid}"` : ""}
      ${this.valid ? `valid="${this.valid}"` : ""}
      ${this.placeholder ? `placeholder="${this.placeholder}"` : ""}
      ${this.maxLength ? `maxlength="${this.maxLength}"` : ""}
      ${this.minLength ? `minlength="${this.minLength}"` : ""}
      ${this.cols ? `cols="${this.cols}"` : ""}
      ${this.rows ? `rows="${this.rows}"` : ""}
      ${this.name ? `name="${this.name}"` : ""}
      ${this.wrap ? `wrap="${this.wrap}"` : ""}
      ${this.value ? `value="${this.value}"` : ""}
      >${this.value || ""}</textarea>`;
  }
  render() {
    return html`
      <div @input=${this.validateInput} class="leaf-c-textarea__wrapper">
        <slot name="textarea"></slot>
        <slot name="note"></slot>
      </div>
      ${this.validation.render()}
    `;
  }
  validateInput() {
    this.validation.validate(this.textarea.value ? this.textarea.value : void 0);
    this.valid = this.validation.validity.valid;
  }
};
_LeafTextarea.styles = unsafeCSS(styles);
_LeafTextarea.elementDefinitions = {
  "leaf-validation-message": LeafValidationMessage
};
_LeafTextarea.formAssociated = true;
let LeafTextarea = _LeafTextarea;
__decorateClass([
  property()
], LeafTextarea.prototype, "autocomplete", 2);
__decorateClass([
  property({ type: Number, attribute: "maxlength" })
], LeafTextarea.prototype, "maxLength", 2);
__decorateClass([
  property({ type: Number, attribute: "minlength" })
], LeafTextarea.prototype, "minLength", 2);
__decorateClass([
  property({ type: Number })
], LeafTextarea.prototype, "cols", 2);
__decorateClass([
  property({ type: Number })
], LeafTextarea.prototype, "rows", 2);
__decorateClass([
  property()
], LeafTextarea.prototype, "placeholder", 2);
__decorateClass([
  property({ reflect: true })
], LeafTextarea.prototype, "error", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafTextarea.prototype, "invalid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafTextarea.prototype, "valid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafTextarea.prototype, "readonly", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafTextarea.prototype, "disabled", 2);
__decorateClass([
  property({ reflect: true })
], LeafTextarea.prototype, "name", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafTextarea.prototype, "required", 2);
__decorateClass([
  property({ reflect: true })
], LeafTextarea.prototype, "wrap", 2);
__decorateClass([
  property()
], LeafTextarea.prototype, "value", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "success-opt-in" })
], LeafTextarea.prototype, "successOptIn", 2);
export {
  LeafTextarea
};
//# sourceMappingURL=textarea.component.js.map
